import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users,
  Plus,
  Search,
  Edit3,
  Trash2,
  EyeOff,
  Eye,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTeamPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/team`);
      setMembers(data || []);
    } catch {
      toast.error("Failed to load team members.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        (m.title || "").toLowerCase().includes(q)
    );
  }, [members, query]);

  const handleDelete = async (member) => {
    if (!window.confirm(`Remove ${member.name} from the team?`)) return;
    try {
      await axios.delete(`${API}/admin/team/${member.id}`);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      toast.success("Team member removed.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const handleTogglePublish = async (member) => {
    try {
      const { data } = await axios.patch(`${API}/admin/team/${member.id}`, {
        published: !member.published,
      });
      setMembers((prev) => prev.map((m) => (m.id === member.id ? { ...m, ...data } : m)));
      toast.success(data.published ? "Published." : "Hidden.");
    } catch {
      toast.error("Update failed.");
    }
  };

  // Swap `member` up or down in the current ordering and persist via /reorder.
  const handleMove = async (member, direction) => {
    const idx = members.findIndex((m) => m.id === member.id);
    if (idx < 0) return;
    const next = direction === "up" ? idx - 1 : idx + 1;
    if (next < 0 || next >= members.length) return;
    const reordered = [...members];
    [reordered[idx], reordered[next]] = [reordered[next], reordered[idx]];
    setMembers(reordered);
    try {
      await axios.post(`${API}/admin/team/reorder`, {
        ids: reordered.map((m) => m.id),
      });
    } catch {
      toast.error("Reorder failed.");
      fetchMembers();
    }
  };

  return (
    <div data-testid="admin-team-page" className="bg-white min-h-full">
      <Toaster position="top-right" richColors />

      <div className="px-5 lg:px-8 py-5 border-b border-slate-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-900">
            <Users className="h-4 w-4 text-sky-600" />
            <h1 className="text-base font-bold">Leadership Team</h1>
            <span className="text-xs text-slate-400">({members.length})</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Team members shown on <span className="font-mono">/why</span>. Use the arrows to reorder, the eye to publish/hide, and the pencil to edit.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search team…"
              className="pl-9 h-9 w-60 text-xs"
              data-testid="admin-team-search"
            />
          </div>
          <Button
            onClick={() => navigate("/admin/team/new")}
            data-testid="admin-team-new"
            className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> New team member
          </Button>
        </div>
      </div>

      <div className="p-5 lg:p-8">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-10">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
            <p className="text-sm text-slate-500">
              {members.length === 0
                ? "No team members yet. Add your first."
                : "No team members match your search."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-sm" data-testid="admin-team-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Order</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Person</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Title</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filtered.map((m, idx) => (
                  <tr
                    key={m.id}
                    data-testid={`admin-team-row-${m.id}`}
                    className="hover:bg-slate-50/50"
                  >
                    <td className="px-4 py-3 w-24">
                      <div className="inline-flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => handleMove(m, "up")}
                          disabled={idx === 0}
                          data-testid={`admin-team-move-up-${m.id}`}
                          className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Move up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMove(m, "down")}
                          disabled={idx === filtered.length - 1}
                          data-testid={`admin-team-move-down-${m.id}`}
                          className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Move down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <span className="text-[10px] text-slate-400 font-mono ml-1">{idx + 1}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        {m.photo_url ? (
                          <img
                            src={m.photo_url}
                            alt=""
                            className="h-9 w-9 rounded-full object-cover border border-slate-200"
                          />
                        ) : (
                          <div className="h-9 w-9 rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold flex items-center justify-center border border-sky-200">
                            {m.initials || m.name.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <p className="text-sm font-semibold text-slate-900 line-clamp-1">{m.name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{m.title}</td>
                    <td className="px-4 py-3">
                      {m.published ? (
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <Eye className="h-2.5 w-2.5" /> Published
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <EyeOff className="h-2.5 w-2.5" /> Hidden
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleTogglePublish(m)}
                          title={m.published ? "Hide" : "Publish"}
                          data-testid={`admin-team-toggle-${m.id}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          {m.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                        <Link
                          to={`/admin/team/${m.id}`}
                          data-testid={`admin-team-edit-${m.id}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(m)}
                          title="Delete"
                          data-testid={`admin-team-delete-${m.id}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
